import React, { useState } from "react";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { SubtitleDescription } from "../subtitleDescription";
import { ProactiveMessagesList } from "./proactiveMessagesList";
import commonStyles from "../../../containers/settings/settingsEditCommon.css";
import styles from "./proactiveMessagesSettings.css";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import classNames from "classnames";
import {
  getLanguageOptionFromBrowser,
  LanguageOption,
  LanguagePicker
} from "@fidget/common/components/form/languagePicker/languagePicker";
import { SystemTag } from "@fidget/common/models/tags";
import { SettingsListFilterProvider } from "../settingsForms/filters/useListFilter/useListFilter";
import { ExistingProactiveFormType } from "./types";
import { withPreloading } from "../../HOC/withPreloading/withPreloading";
import { withPermissionFallback } from "../../HOC/withPermissionFallback/withPermissionFallback";
import { AppPermission } from "@closerplatform/spinner-openapi";

const ProactiveMessagesSettingsComponent = () => {
  const { currentLanguage, setCurrentLanguage } = useProactiveMessageSettingsState();

  const renderListDescription = () => {
    return (
      <div>
        {intl.formatMessage("settings.proactiveMessages.messageList.description")}
        <TooltipComponent
          text={intl.formatMessage("settings.tags.newVisitor.description")}
          positionPreset="top"
          style="dashboard"
          displayInline
          minWidth="90px"
        >
          <p className={classNames(styles["system-tag"], styles["system-tag-comma-space"])}>
            {SystemTag.NewVisitor}
          </p>
        </TooltipComponent>
        <TooltipComponent
          text={intl.formatMessage("settings.tags.returningVisitor.description")}
          positionPreset="top"
          style="dashboard"
          displayInline
          minWidth="90px"
        >
          <p className={classNames(styles["system-tag"], styles["system-tag-comma-space"])}>
            {SystemTag.ReturningVisitor}
          </p>
        </TooltipComponent>
        <TooltipComponent
          text={intl.formatMessage("settings.tags.exitIntent.description")}
          positionPreset="top"
          style="dashboard"
          displayInline
          minWidth="90px"
        >
          <p className={styles["system-tag"]}>{SystemTag.ExitIntent}</p>
        </TooltipComponent>
        <br />
        {intl.formatMessage("settings.proactiveMessages.messageList.descriptionContinued")}
      </div>
    );
  };

  return (
    <div>
      <div className={commonStyles.title}>
        <L10n id={"settings.admin.proactiveMessages"} />
      </div>
      <div className={commonStyles.section}>
        <SubtitleDescription
          subtitle={intl.formatMessage("settings.languagePicker.title")}
          description={intl.formatMessage("settings.proactiveMessages.language.description")}
        />
        <div className={commonStyles.content}>
          <LanguagePicker
            onChange={(val) => val && setCurrentLanguage(val)}
            currentLanguage={currentLanguage}
          />
        </div>
      </div>
      <div className={commonStyles.section}>
        <SubtitleDescription
          subtitle={intl.formatMessage("settings.proactiveMessages.messageList")}
          descriptionElement={renderListDescription()}
        />
        <div className={commonStyles.content}>
          <SettingsListFilterProvider<ExistingProactiveFormType>>
            <ProactiveMessagesList pickedLanguage={currentLanguage} />
          </SettingsListFilterProvider>
        </div>
      </div>
    </div>
  );
};

const useProactiveMessageSettingsState = () => {
  const [currentLanguage, setCurrentLanguage] = useState<LanguageOption>(
    getLanguageOptionFromBrowser()
  );

  return {
    currentLanguage,
    setCurrentLanguage
  };
};

export const ProactiveMessagesSettings = withPermissionFallback<{}>(
  AppPermission.SettingsProactiveMessages,
  withPreloading(ProactiveMessagesSettingsComponent)
);
